import React, { useEffect, useRef, useState } from 'react';
import {
    IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
    IonContent, IonIcon, IonInput, IonSelect, IonSelectOption, IonText,
    IonAlert, IonSpinner, IonBadge,
} from '@ionic/react';
import { addOutline, closeOutline, createOutline, checkmarkOutline, removeCircleOutline } from 'ionicons/icons';
import { LookupRowFieldConfig, LookupCrudModalProps } from '@/types/model/lookupCrudModal';
import TransactionReasonModal from './TransactionReasonModal';
import Toaster, { ToasterVariant } from './Toaster';

type PendingAction<TRow, TItem> =
    | { type: 'save'; row: TRow }
    | { type: 'activate'; item: TItem; activate: boolean };

function LookupCrudModal<TItem, TRow extends object>({
    isOpen, onDidDismiss, title, items, columns, rowFields, emptyRow, getEditingId, toRow,
    onSaveRow, onSaved, saveReasonTitle, activateAction, width = 'min(80vw, 1400px)', height = '90vh',
}: LookupCrudModalProps<TItem, TRow>) {
    const [rowForm, setRowForm] = useState<TRow | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showExitAlert, setShowExitAlert] = useState(false);
    const [pendingAction, setPendingAction] = useState<PendingAction<TRow, TItem> | null>(null);
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [toast, setToast] = useState<{ message: string; variant: ToasterVariant } | null>(null);
    const forceClosingRef = useRef(false);

    useEffect(() => {
        if (isOpen) {
            forceClosingRef.current = false;
            setRowForm(null);
            setEditingId(null);
        }
    }, [isOpen]);

    const handleClose = () => {
        if (forceClosingRef.current) return;
        if (rowForm != null) setShowExitAlert(true);
        else onDidDismiss();
    };

    const handleForceClose = () => {
        setShowExitAlert(false);
        forceClosingRef.current = true;
        onDidDismiss();
    };

    const updateRow = (key: keyof TRow, value: unknown) => {
        setRowForm(prev => prev ? { ...prev, [key]: value } : null);
        setErrors(prev => ({ ...prev, [key as string]: '' }));
    };

    const validateRow = (): boolean => {
        if (!rowForm) return false;
        const newErrors: Record<string, string> = {};
        rowFields.forEach(field => {
            const message = field.validate?.(rowForm[field.key], rowForm);
            if (message) newErrors[field.key as string] = message;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAddNew = () => {
        setEditingId(null);
        setRowForm({ ...emptyRow });
        setErrors({});
    };

    const handleEditRow = (item: TItem) => {
        setEditingId(getEditingId(item));
        setRowForm(toRow(item));
        setErrors({});
    };

    const handleSaveRowClick = () => {
        if (!validateRow() || !rowForm) return;
        setPendingAction({ type: 'save', row: rowForm });
        setShowReasonModal(true);
    };

    const handleActivateClick = (item: TItem, activate: boolean) => {
        setPendingAction({ type: 'activate', item, activate });
        setShowReasonModal(true);
    };

    const getReasonTitle = (): string => {
        if (!pendingAction) return '';
        if (pendingAction.type === 'save') return saveReasonTitle(pendingAction.row);
        return activateAction?.reasonTitle(pendingAction.item, pendingAction.activate) ?? '';
    };

    const handleReasonConfirm = async (reason: string) => {
        setShowReasonModal(false);
        if (!pendingAction) return;
        setIsSaving(true);
        try {
            if (pendingAction.type === 'save') {
                await onSaveRow(pendingAction.row, reason);
                setRowForm(null);
                setEditingId(null);
                setToast({ message: 'Saved successfully.', variant: 'success' });
            } else if (activateAction) {
                await activateAction.onToggle(pendingAction.item, pendingAction.activate, reason);
                setToast({ message: pendingAction.activate ? 'Activated.' : 'Inactivated.', variant: 'success' });
            }
            onSaved?.();
        } catch {
            setToast({ message: 'Failed to save changes.', variant: 'error' });
        } finally {
            setIsSaving(false);
            setPendingAction(null);
        }
    };

    const renderRowInput = (field: LookupRowFieldConfig<TRow>) => {
        if (!rowForm) return null;
        const value = rowForm[field.key];
        if (field.type === 'select') {
            return (
                <IonSelect value={String(value ?? '')} onIonChange={e => updateRow(field.key, e.detail.value)}
                    style={{ '--padding-start': '0.5rem', border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }}>
                    {field.options?.map(o => <IonSelectOption key={o.value} value={o.value}>{o.label}</IonSelectOption>)}
                </IonSelect>
            );
        }
        return (
            <IonInput
                type={field.type === 'number' ? 'number' : 'text'}
                value={String(value ?? '')}
                placeholder={field.placeholder ?? field.label}
                onIonInput={e => updateRow(field.key, e.detail.value ?? '')}
                style={{ '--padding-start': '0.5rem', border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }}
            />
        );
    };

    const alignStyle = (align?: 'left' | 'center' | 'right'): React.CSSProperties => ({
        textAlign: align ?? 'left',
    });

    return (
        <>
            <IonModal isOpen={isOpen} onDidDismiss={handleClose} style={{ '--width': width, '--height': height, '--max-width': '1400px', '--border-radius': '8px' }}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle style={{ fontSize: '1rem' }}>{title}</IonTitle>
                        <IonButtons slot="end">
                            {rowForm == null && (
                                <IonButton onClick={handleAddNew} title="Add New">
                                    <IonIcon icon={addOutline} />
                                </IonButton>
                            )}
                            <IonButton onClick={handleClose}><IonIcon icon={closeOutline} /></IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                <IonContent style={{ '--padding-start': '1rem', '--padding-end': '1rem', '--padding-top': '1rem', '--padding-bottom': '1rem' }}>
                    {isSaving ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                            <IonSpinner />
                        </div>
                    ) : (
                        <>
                            {rowForm != null && (
                                <div style={{ background: 'var(--ion-color-light)', borderRadius: '6px', padding: '1rem', margin: '1rem', border: '1px solid var(--ion-color-light-shade)' }}>
                                    <div style={{ fontWeight: '600', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                                        {editingId != null ? 'Edit' : 'New'}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(rowFields.length, 3)}, 1fr)`, gap: '0.75rem', marginBottom: '0.5rem' }}>
                                        {rowFields.map(field => (
                                            <div key={field.key as string} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium)', minWidth: '100px', flex: '0 0 auto', paddingTop: '0.25rem' }}>{field.label}</div>
                                                <div style={{ flex: '1', minWidth: '110px' }}>
                                                    {renderRowInput(field)}
                                                    {errors[field.key as string] && (
                                                        <IonText color="danger" style={{ fontSize: '0.75rem', display: 'block' }}>{errors[field.key as string]}</IonText>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                        <IonButton fill="outline" color="medium" size="small" onClick={() => setRowForm(null)}>Cancel</IonButton>
                                        <IonButton fill="solid" color="primary" size="small" onClick={handleSaveRowClick}>Save</IonButton>
                                    </div>
                                </div>
                            )}

                            {rowForm == null && (items.length === 0 ? (
                                <IonText color="medium" style={{ display: 'block', textAlign: 'center', padding: '2rem' }}>
                                    No records defined.
                                </IonText>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--ion-color-light-shade)' }}>
                                            {columns.map(col => (
                                                <th key={col.key as string} style={{ padding: '0.5rem', fontSize: '0.85rem', color: 'var(--ion-color-medium)', fontWeight: '700', ...alignStyle(col.align) }}>
                                                    {col.label}
                                                </th>
                                            ))}
                                            {activateAction && <th style={{ textAlign: 'center', padding: '0.5rem', fontSize: '0.85rem', color: 'var(--ion-color-medium)', fontWeight: '700' }}>Status</th>}
                                            <th style={{ width: '90px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map(item => (
                                            <tr key={getEditingId(item)} style={{ borderBottom: '1px solid var(--ion-color-light-shade)' }}>
                                                {columns.map(col => (
                                                    <td key={col.key as string} style={{ padding: '0.5rem', ...alignStyle(col.align) }}>
                                                        {col.format ? col.format(item) : String(item[col.key] ?? '')}
                                                    </td>
                                                ))}
                                                {activateAction && (
                                                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                                                        <IonBadge color={activateAction.isActive(item) ? 'success' : 'medium'}>
                                                            {activateAction.isActive(item) ? 'Active' : 'Inactive'}
                                                        </IonBadge>
                                                    </td>
                                                )}
                                                <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    <IonButton fill="clear" size="small" onClick={() => handleEditRow(item)} disabled={rowForm != null}>
                                                        <IonIcon icon={createOutline} />
                                                    </IonButton>
                                                    {activateAction && (activateAction.isActive(item) ? (
                                                        <IonButton fill="clear" size="small" color="warning" onClick={() => handleActivateClick(item, false)} disabled={rowForm != null}>
                                                            <IonIcon icon={removeCircleOutline} />
                                                        </IonButton>
                                                    ) : (
                                                        <IonButton fill="clear" size="small" color="success" onClick={() => handleActivateClick(item, true)} disabled={rowForm != null}>
                                                            <IonIcon icon={checkmarkOutline} />
                                                        </IonButton>
                                                    ))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ))}
                        </>
                    )}
                </IonContent>
            </IonModal>

            <IonAlert isOpen={showExitAlert} header="Unsaved Changes" message="You have an unsaved row. Close anyway?"
                buttons={[
                    { text: 'Keep Editing', role: 'cancel', handler: () => setShowExitAlert(false) },
                    { text: 'Close', role: 'destructive', handler: handleForceClose },
                ]}
                onDidDismiss={() => setShowExitAlert(false)}
            />
            <Toaster isOpen={!!toast} message={toast?.message} variant={toast?.variant} onDidDismiss={() => setToast(null)} />
            <TransactionReasonModal isOpen={showReasonModal} title={getReasonTitle()}
                onConfirm={handleReasonConfirm} onCancel={() => { setShowReasonModal(false); setPendingAction(null); }} />
        </>
    );
}

export default LookupCrudModal;
